import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { traccarGetTemplates, traccarSendCommand } from '../../gps/utils/traccar';
import { sendTelegramNotification } from '../../shared/utils/helpers';
import { buildTelegramMessage } from '../../../utils/telegramMessages';
import Modal from '../../shared/components/Modal';

export default function EngineControlModal({ car, settings, onClose }) {
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [customCommand, setCustomCommand] = useState('');

  const traccarDeviceId = car.traccarDeviceId || car.gpsDeviceId;
  const traccarUniqueId = car.traccarUniqueId;
  
  // Detect saved working preset for this device
  const savedPresetKey = `gps_preset_${traccarUniqueId}`;
  const savedPreset = (() => {
    try { return localStorage.getItem(savedPresetKey); } catch { return null; }
  })();

  const ALL_PRESETS = [
    { id: 'coban_tk103', name: 'Coban TK103', off: 'relay,1#', on: 'relay,0#' },
    { id: 'coban_tk103_alt', name: 'Coban TK103 (alt)', off: 'relay,1', on: 'relay,0' },
    { id: 'sinotrack', name: 'SinoTrack (ST-901 / TK905)', off: '9400000', on: '9410000' },
    { id: 'lk720', name: 'LK720 / Leka', off: '555', on: '666' },
    { id: 'concox_gt06', name: 'Concox GT06', off: 'RELAY,1#', on: 'RELAY,0#' },
    { id: 'concox_alt', name: 'Concox (alt)', off: 'DYD,123456#', on: 'HFYD,123456#' },
    { id: 'teltonika', name: 'Teltonika FMB', off: 'setdigout 1 0 0 0', on: 'setdigout 0 0 0 0' },
    { id: 'teltonika_alt', name: 'Teltonika (alt)', off: 'setdigout 1', on: 'setdigout 0' },
    { id: 'coban_gps103', name: 'Coban GPS103', off: 'stop123456', on: 'resume123456' },
    { id: 'queclink', name: 'Queclink GV', off: 'AT+GTOUT=gv200,1,,,,,,,,,,,,FFFF$', on: 'AT+GTOUT=gv200,0,,,,,,,,,,,,FFFF$' },
    { id: 'meitrack', name: 'Meitrack', off: '@@a25,123,K41,1*BC\r\n', on: '@@a25,123,K41,0*BD\r\n' },
    { id: 'huabao', name: 'Huabao JT808', off: 'CUTOIL#', on: 'RECOIL#' }
  ];

  useEffect(() => {
    const loadTemplates = async () => {
      if (!traccarDeviceId && !traccarUniqueId) {
        setLoading(false);
        return;
      }
      try {
        const tmpl = await traccarGetTemplates(settings, traccarDeviceId || traccarUniqueId);
        setTemplates(tmpl || []);
      } catch (err) {
        console.error('Load templates failed:', err);
      } finally {
        setLoading(false);
      }
    };
    loadTemplates();
  }, [settings, traccarDeviceId, traccarUniqueId]);

  // Auto-detect fuel pump OFF/ON templates by name
  const pumpOffTemplate = templates.find(t => {
    const name = (t.title || t.name || '').toLowerCase();
    return name.includes('off') || name.includes('stop') || name.includes('block') || 
           name.includes('relay on') || name.includes('cut') || name.includes('إيقاف') ||
           name.includes('couper') || name.includes('arrêt') || name.includes('arret');
  });
  
  const pumpOnTemplate = templates.find(t => {
    const name = (t.title || t.name || '').toLowerCase();
    return name.includes('start') || (name.includes('on') && !name.includes('off')) ||
           name.includes('unblock') || name.includes('resume') || name.includes('relay off') ||
           name.includes('démarrer') || name.includes('demarrer') || name.includes('marche') ||
           name.includes('تشغيل') || name.includes('rétablir') || name.includes('retablir');
  });

  const handleSendCommand = async (command, label) => {
    if (!command) return;
    setSending(true);
    setLastResult(null);
    try {
      const result = await traccarSendCommand(settings, traccarDeviceId || traccarUniqueId, command);
      
      if (result.success) {
        setLastResult({ 
          ok: true, 
          message: isAr 
            ? `✅ تم إرسال الأمر بنجاح!`
            : `✅ Commande envoyée avec succès!`
        });
        
        await sendTelegramNotification(
          buildTelegramMessage('engine_control', {
            brand: car.brand, model: car.model, plate: car.plate,
            action: label, command, time: new Date().toLocaleString('fr-FR')
          }),
          'engine_control'
        );
        
      } else {
        setLastResult({ 
          ok: false, 
          message: `❌ ${result.error || 'Erreur inconnue'}`
        });
      }
    } catch (err) {
      setLastResult({ ok: false, message: '❌ ' + err.message });
    } finally {
      setSending(false);
    }
  };

  const handleActionClick = async (action) => {
    const label = action === 'off' 
      ? (isAr ? 'هل تريد إيقاف مضخة البنزين؟ (STOP)' : 'Voulez-vous couper la pompe à essence ? (STOP)')
      : (isAr ? 'هل تريد تشغيل مضخة البنزين؟ (DÉMARRER)' : 'Voulez-vous rétablir la pompe à essence ? (DÉMARRER)');
      
    if (!window.confirm(label)) return;

    const template = getTemplateForAction(action);
    let cmdToSend = getTemplateCmd(template);
    if (!cmdToSend && savedPreset) {
      const preset = ALL_PRESETS.find(p => p.id === savedPreset);
      if (preset) cmdToSend = action === 'off' ? preset.off : preset.on;
    }
    // Default fallback to Coban TK103 if no preset or template is found
    if (!cmdToSend) {
      cmdToSend = action === 'off' ? 'relay,1#' : 'relay,0#';
    }

    await handleSendCommand(cmdToSend, action === 'off' ? 'Couper' : 'Rétablir');
  };

  if (!traccarUniqueId) {
    return (
      <Modal onClose={onClose} title={`⛽ ${isAr ? 'التحكم في مضخة البنزين' : 'Contrôle Pompe Essence'}`}>
        <div className="p-4">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">⚠️</span>
              <span className="font-bold">{isAr ? 'لا يوجد جهاز GPS مرتبط' : 'Aucun appareil GPS lié'}</span>
            </div>
            <p className="text-amber-800">
              {isAr 
                ? 'لاستخدام هذه الميزة، يجب ربط هذه السيارة بجهاز GPS أولاً.'
                : 'Liez d\'abord cette voiture à un appareil GPS (Modifier voiture).'}
            </p>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 rounded-lg font-bold hover:bg-gray-300 transition text-gray-700 text-sm"
            >
              {isAr ? 'إغلاق' : 'Fermer'}
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  const getTemplateCmd = (template) => {
    if (!template) return '';
    
    // If it's a template from the server, we should use its ID to build the template command name (e.g., template_11)
    if (template.id) {
      const idStr = String(template.id);
      if (idStr.startsWith('template_')) {
        return idStr;
      }
      if (/^\d+$/.test(idStr)) {
        return `template_${idStr}`;
      }
    }
    
    if (template.type && String(template.type).startsWith('template_')) {
      return template.type;
    }
    
    return template.message || template.data || template.command || template.body || template.gprs || '';
  };

  const getTemplateForAction = (action) => action === 'off' ? pumpOffTemplate : pumpOnTemplate;
  const hasTemplate = (action) => {
    const t = getTemplateForAction(action);
    return !!(t && getTemplateCmd(t));
  };

  return (
    <Modal onClose={onClose} title={`⛽ ${isAr ? 'التحكم في مضخة البنزين' : 'Contrôle Pompe Essence'}`}>
      <div>
        {/* Car info banner */}
        <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
          <div className="flex items-center gap-3">
            <span className="text-3xl">🚗</span>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900">{`${car.brand} ${car.model}`}</h3>
              <p className="text-xs text-gray-600 font-mono">{car.plate}</p>
              <p className="text-[10px] text-gray-500 font-mono mt-1">📡 Device: {traccarUniqueId}</p>
            </div>
            {car.gpsDeviceStatus === 'online' ? (
              <span className="px-2 py-1 bg-green-200 text-green-900 rounded-full text-xs font-bold">🟢 Online</span>
            ) : (
              <span className="px-2 py-1 bg-gray-200 text-gray-700 rounded-full text-xs font-bold">⚫ Offline</span>
            )}
          </div>
        </div>
        
        {/* Loading state */}
        {loading && (
          <div className="mb-4 text-center text-sm text-gray-500 py-4">
            ⏳ {isAr ? 'جاري التحميل...' : 'Chargement...'}
          </div>
        )}

        {/* Buttons */}
        {!loading && (
          <div className="grid grid-cols-2 gap-3 mb-4">
            <button
              onClick={() => handleActionClick('off')}
              disabled={sending}
              className="p-5 rounded-xl font-bold text-white transition-all shadow-lg bg-red-600 hover:bg-red-700 active:scale-95 disabled:bg-gray-400 disabled:scale-100 flex flex-col items-center justify-center"
            >
              <span className="text-4xl mb-2">🛑</span>
              <span className="text-sm font-black">STOP / OFF</span>
              <span className="text-[10px] mt-1 opacity-70">
                {isAr ? 'قطع مضخة البنزين (إيقاف)' : 'Stop / Couper Pompe Essence'}
              </span>
            </button>
            <button
              onClick={() => handleActionClick('on')}
              disabled={sending}
              className="p-5 rounded-xl font-bold text-white transition-all shadow-lg bg-green-600 hover:bg-green-700 active:scale-95 disabled:bg-gray-400 disabled:scale-100 flex flex-col items-center justify-center"
            >
              <span className="text-4xl mb-2">⚡</span>
              <span className="text-sm font-black">DÉMARRER / ON</span>
              <span className="text-[10px] mt-1 opacity-70">
                {isAr ? 'تشغيل مضخة البنزين (بدء)' : 'Démarrer / Rétablir Pompe Essence'}
              </span>
            </button>
          </div>
        )}
        
        {/* Preset indicator */}
        {savedPreset && (
          <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between gap-2 text-xs">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="text-lg flex-shrink-0">✅</span>
              <div className="min-w-0">
                <div className="font-bold text-blue-900">{isAr ? 'الأمر المحفوظ:' : 'Préset sauvegardé:'}</div>
                <div className="text-blue-700 font-mono text-[11px] truncate">
                  {ALL_PRESETS.find(p => p.id === savedPreset)?.name || savedPreset}
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                try { localStorage.removeItem(savedPresetKey); } catch {}
                setLastResult(null);
                // force component rerender by toggling preset value
                window.location.reload();
              }}
              className="text-[10px] text-blue-700 hover:bg-blue-100 px-2 py-1 rounded font-bold flex-shrink-0 transition"
            >
              {isAr ? 'مسح' : 'Effacer'}
            </button>
          </div>
        )}
        
        {/* Output result message */}
        {lastResult && (
          <div className={`mb-3 p-3 rounded-lg text-sm font-bold border ${
            lastResult.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            {lastResult.message}
          </div>
        )}

        {/* Custom Command Input */}
        {!loading && (
          <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-xl">
            <h4 className="font-bold text-gray-800 mb-2 text-xs flex items-center gap-1.5 uppercase tracking-wide">
              <span>⌨️</span> {isAr ? 'أمر مخصص (إرسال يدوي)' : 'Commande personnalisée (Saisie manuelle)'}
            </h4>
            <input
              type="text"
              value={customCommand}
              onChange={(e) => setCustomCommand(e.target.value)}
              placeholder={isAr ? 'مثال: 9400000 أو relay,1#' : 'Ex: 9400000 ou relay,1#'}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg font-mono focus:ring-2 focus:ring-blue-500 bg-white"
            />
            <div className="grid grid-cols-2 gap-2 mt-3">
              <button
                type="button"
                onClick={() => {
                  if (!customCommand.trim()) return alert(isAr ? 'يرجى إدخال الأمر أولاً' : 'Veuillez saisir la commande');
                  handleSendCommand(customCommand.trim(), isAr ? 'قطع مخصص' : 'Couper personnalisé');
                }}
                disabled={sending || !customCommand.trim()}
                className="py-2.5 px-3 bg-red-650 hover:bg-red-700 text-white rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 transition shadow-sm disabled:bg-gray-300 disabled:text-gray-500 cursor-pointer"
              >
                🛑 {isAr ? 'إرسال إيقاف (STOP)' : 'Envoyer STOP'}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!customCommand.trim()) return alert(isAr ? 'يرجى إدخال الأمر أولاً' : 'Veuillez saisir la commande');
                  handleSendCommand(customCommand.trim(), isAr ? 'تشغيل مخصص' : 'Rétablir personnalisé');
                }}
                disabled={sending || !customCommand.trim()}
                className="py-2.5 px-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 transition shadow-sm disabled:bg-gray-300 disabled:text-gray-500 cursor-pointer"
              >
                ⚡ {isAr ? 'إرسال تشغيل (ON)' : 'Envoyer DÉMARRER'}
              </button>
            </div>
          </div>
        )}

        {/* Protocol Selector */}
        {!loading && (
          <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs">
            <label className="block font-bold text-gray-700 mb-1">
              {isAr ? 'نوع جهاز الـ GPS:' : 'Type de traceur GPS :'}
            </label>
            <select
              value={savedPreset || 'coban_tk103'}
              onChange={(e) => {
                const val = e.target.value;
                try { localStorage.setItem(savedPresetKey, val); } catch {}
                // Force component refresh
                window.location.reload();
              }}
              className="w-full p-2 border border-gray-300 rounded-lg font-medium bg-white outline-none focus:ring-2 focus:ring-amber-500"
            >
              {ALL_PRESETS.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <p className="text-[10px] text-gray-500 mt-1">
              {isAr ? 'ملاحظة: سيتم استخدام الأوامر الخاصة بهذا النوع تلقائياً عند الضغط.' : 'Note : Les commandes de ce modèle seront envoyées automatiquement.'}
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}
